import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../api.js";
import { loadKey, signBytes } from "../../kep.js";
import { parsePnXml } from "../../../../lib/pn/parse.js";
import { pnFormHtml } from "../../../../lib/pn/form.js";
import { decodeCp1251 } from "../../../../lib/pn/cp1251.js";
import formCssUrl from "../../../../lib/pn/form.css?url";
import { ScaledForm, TextVersion } from "./PnForm.jsx";
import CertField from "../../CertField.jsx";

// Підпис і реєстрація (план §7). Кабінет перебудовує документ після завантаження, тож підписуємо байти,
// які він дав на підпис, — після того як людина переглянула бланк саме з них. Ключ і пароль лишаються в
// браузері; на сервер іде лише підпис. «Зареєстровано» показуємо лише за квитанцією ДПС.
const b64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
// Поля, яких немає на бланку: їхня зміна кабінетом не змінює надруковану накладну
const SERVICE = new Set(["SOFTWARE", "HSTI", "D_FILL", "C_DOC_CNT", "LINKED_DOCS"]);
const LABEL = { SOFTWARE: "назва програми", HSTI: "назва податкової інспекції", D_FILL: "дата формування файлу", C_DOC_CNT: "службовий номер документа в періоді" };

const STEPS = [
  ["dps_draft", "Документ у кабінеті ДПС"], ["confirmed", "Переглянуто"], ["sent", "Підписано й надіслано"], ["registered", "Зареєстровано в ЄРПН"],
];
const ORDER = { creating: 0, dps_draft: 0, confirmed: 1, signing: 2, sending: 2, sent: 2, registered: 3, stopped: 3, rejected: 3, unknown: 2, failed: 0 };

export default function PnSign({ companyId, draftId, company, ourXml, onRelogin, onLocked, onStarted }) {
  const [sub, setSub] = useState(undefined);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const canSubmit = ["submitter", "owner"].includes(company.role);
  const base = `/api/companies/${companyId}/pn`;

  // stale — чернетка в кабінеті для попередньої версії накладної (її потім змінили): підписувати її не можна.
  // Перечитуємо і після змін накладної (ourXml), а не лише при відкритті
  const [stale, setStale] = useState(null);
  useEffect(() => { api(`${base}/drafts/${draftId}/submission`).then((r) => { setSub(r.submission); setStale(r.stale || null); }, setError); }, [draftId, ourXml]);
  // Подання почалось (є документ ДПС) — локальний бланк більше не потрібен: підписуємо бланк ДПС
  useEffect(() => { onStarted?.(Boolean(sub && sub.state !== "failed")); }, [sub?.state]);
  useEffect(() => { if (sub && ["signing", "sending", "sent", "registered", "stopped", "unknown"].includes(sub.state)) onLocked?.(); }, [sub?.state]);
  // Поки чекаємо квитанцію — перевіряємо стан у ДПС кожні 20 с (лише читання)
  useEffect(() => {
    if (sub?.state !== "sent") return;
    const t = setInterval(() => run("refresh", () => api(`${base}/submissions/${sub.id}/refresh`, { method: "POST" })), 20000);
    return () => clearInterval(t);
  }, [sub?.state, sub?.id]);

  const run = async (what, fn) => {
    setBusy(what); setError(null);
    try { const r = await fn(); if (r?.submission) setSub(r.submission); return r; }
    catch (e) { setError(e); if (e instanceof ApiError && e.status !== 409) api(`${base}/drafts/${draftId}/submission`).then((r) => setSub(r.submission)).catch(() => {}); }
    finally { setBusy(null); }
  };
  const start = () => run("start", () => api(`${base}/drafts/${draftId}/submission`, { method: "POST" }));
  // «Зберегти проект ПН» — той самий виклик: ПН стає чернеткою в кабінеті ДПС (без підпису й відправлення);
  // підписати можна пізніше тут або в кабінеті ДПС. Повторне натискання другої чернетки не створює
  const [saved, setSaved] = useState(false);
  const saveProject = async () => { const r = await run("save", () => api(`${base}/drafts/${draftId}/submission`, { method: "POST" })); if (r?.submission?.dpsDocId) setSaved(true); };

  if (sub === undefined) return <p className="muted" role="status">Перевіряємо стан подання…</p>;
  const err = error && (
    <p className="error" role="alert">{error.message}
      {error.message.includes("Сесія ДПС") && onRelogin && <> <button type="button" className="linkish" onClick={onRelogin}>Увійти ключем</button></>}</p>
  );

  if (!sub || (sub.state === "failed" && !sub.dpsDocId)) {
    return (
      <div className="pn-sign">
        {sub?.state === "failed" && <p className="card warn" role="alert">Попередня спроба не вдалася: {sub.reason}. У кабінеті документ не створено — можна спробувати ще раз.</p>}
        {!sub && stale && <p className="card warn" role="alert">Накладну змінено після збереження в кабінеті ДПС: там лежить попередня версія (чернетка № {stale.dpsDocId}).
          Збережіть накладну ще раз — створимо чернетку з поточними даними, а попередню видалимо з кабінету.</p>}
        <p>Щоб підписати, створимо цю накладну в кабінеті ДПС як чернетку (без підпису й відправлення) і покажемо документ,
          який кабінет дасть на підпис. Кабінет трохи змінює службові поля — ви побачите, які саме.</p>
        {err}
        <div className="actions">
          <button type="button" className="btn btn-secondary" disabled={!canSubmit || Boolean(busy)} onClick={saveProject} aria-describedby="pn-save-project-hint">
            {busy === "save" ? "Зберігаємо в кабінеті ДПС…" : "Зберегти проект ПН"}</button>
          <button type="button" className="btn" disabled={!canSubmit || Boolean(busy)} onClick={start}>{busy === "start" ? "Створюємо чернетку в кабінеті…" : "Підписати й зареєструвати"}</button>
          {!canSubmit && <span className="muted small">Зберігати в кабінеті ДПС і подавати можуть ролі «подання» і «власник».</span>}
        </div>
        <p className="muted small" id="pn-save-project-hint">«Зберегти проект ПН» — накладна збережеться в кабінеті ДПС як чернетка, без підпису й відправлення.
          Підписати її можна пізніше — тут або в кабінеті ДПС.</p>
      </div>
    );
  }
  return (
    <div className="pn-sign">
      <ol className="pn-track" aria-label="Етапи подання">
        {STEPS.map(([k, l], i) => <li key={k} className={ORDER[sub.state] > i ? "done" : ORDER[sub.state] === i ? "on" : ""}>{l}</li>)}
      </ol>
      {err}
      {sub.dpsDocId && ["dps_draft", "confirmed"].includes(sub.state) && (
        <p className={saved ? "pn-ok" : "small"} role="status">{saved ? "✓ " : ""}Проект ПН збережено в кабінеті ДПС як чернетку (документ № {sub.dpsDocId}), без підпису й відправлення.
          Підписати можна нижче зараз або пізніше — тут чи в кабінеті ДПС.</p>
      )}
      {sub.state === "failed" && sub.phase === "p7s" && (
        <p className="card warn" role="alert">ДПС не прийняла підпис: {sub.reason}. Накладна в кабінеті лишилась непідписаною — можна підписати ще раз.</p>
      )}
      {(sub.state === "dps_draft" || sub.state === "confirmed" || (sub.state === "failed" && sub.phase === "p7s")) && (
        <DpsReview sub={sub} ourXml={ourXml} base={base} busy={busy} run={run} canSubmit={canSubmit} company={company} />
      )}
      {["signing", "sending"].includes(sub.state) && <p role="status">Надсилаємо документ у ДПС…</p>}
      {["sent", "registered", "stopped", "rejected"].includes(sub.state) && <Result sub={sub} />}
      {sub.state === "unknown" && (
        <div className="card warn" role="alert">
          <b>Результат невідомий.</b> Зв'язок із ДПС обірвався на кроці «{({ new: "створення чернетки", upload: "завантаження", p7s: "підпис", send: "відправлення" })[sub.phase] || sub.phase}».
          Документ міг бути прийнятий. Повторно не подаємо, щоб не задвоїти накладну. Перевірте стан у ДПС{sub.dpsDocId ? "" : " (у кабінеті — розділ «Податкові накладні», чернетки й відправлені)"}.
        </div>
      )}
      {sub.state === "failed" && sub.phase !== "p7s" && <p className="card warn" role="alert">ДПС відмовила на кроці «{sub.phase}»: {sub.reason}</p>}
      {sub.dpsDocId && !["dps_draft", "confirmed"].includes(sub.state) && !(sub.state === "failed" && sub.phase === "p7s") && (
        <div className="actions">
          <button type="button" className="btn btn-secondary btn-sm" disabled={Boolean(busy)} onClick={() => run("refresh", () => api(`${base}/submissions/${sub.id}/refresh`, { method: "POST" }))}>
            {busy === "refresh" ? "Перевіряємо…" : "Перевірити стан у ДПС"}</button>
          {sub.checkedAt && <span className="muted small">перевірено {new Date(sub.checkedAt).toLocaleString("uk-UA")}</span>}
        </div>
      )}
    </div>
  );
}

// Документ ДПС: бланк із байтів, які підписуватимуться, і що змінив кабінет; підтвердження; ключ і підпис
function DpsReview({ sub, ourXml, base, busy, run, canSubmit, company }) {
  const bytes = useMemo(() => b64ToBytes(sub.dpsXml), [sub.dpsXml]);
  const text = useMemo(() => decodeCp1251(bytes), [bytes]);
  const parsed = useMemo(() => parsePnXml(text), [text]);
  const html = useMemo(() => pnFormHtml(parsed, { cssHref: new URL(formCssUrl, location.href).href }), [parsed]);
  const [hash, setHash] = useState(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => { crypto.subtle.digest("SHA-256", bytes).then((b) => setHash(hex(b))); }, [bytes]);
  const diff = useMemo(() => {
    if (!ourXml) return [];
    const a = parsePnXml(ourXml).fields, b = parsed.fields, out = [];
    for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) if ((a[k] ?? "") !== (b[k] ?? "")) out.push({ k, from: a[k] ?? "", to: b[k] ?? "", service: SERVICE.has(k) });
    return out;
  }, [ourXml, parsed]);
  const printed = diff.filter((d) => !d.service);
  const hashOk = hash === sub.dpsSha256;
  const openPdf = async () => {
    const r = await fetch(`${base}/submissions/${sub.id}/pdf`, { credentials: "same-origin" });
    if (!r.ok) return alert("ДПС не віддала бланк");
    window.open(URL.createObjectURL(await r.blob()), "_blank", "noopener");
  };
  return (
    <>
      <section className="card" aria-labelledby="pn-dps-h">
        <h3 id="pn-dps-h">Документ, який ви підпишете</h3>
        <p>Це накладна в тому вигляді, в якому її зберіг кабінет ДПС (чернетка № {sub.dpsDocId}). Підписуються саме ці байти.</p>
        {printed.length > 0 ? (
          <p className="error" role="alert">Кабінет змінив друковані реквізити: {printed.map((d) => `${d.k}: «${d.from}» → «${d.to}»`).join("; ")}. Перевірте уважно.</p>
        ) : (
          <p className="pn-ok">✓ Друковані реквізити збігаються з переглянутою накладною.</p>
        )}
        {diff.filter((d) => d.service).length > 0 && (
          <p className="muted small">Службові поля, яких немає на бланку, кабінет заповнив сам: {diff.filter((d) => d.service).map((d) => `${LABEL[d.k] || d.k} — «${d.to}»`).join(", ")}.</p>
        )}
        <div className="actions"><button type="button" className="btn btn-secondary btn-sm" onClick={openPdf}>Бланк із ДПС (PDF)</button></div>
      </section>
      <ScaledForm html={html} />
      <TextVersion parsed={parsed} />
      <div className="card pn-confirm">
        <p className="small muted">Документ ДПС · {bytes.length} байт · SHA-256 <span className="mono-sm">{sub.dpsSha256.slice(0, 12)}…{sub.dpsSha256.slice(-6)}</span>{hashOk ? " · збігається з показаним" : ""}</p>
        {sub.state === "dps_draft" ? (
          <>
            <label className="check"><input type="checkbox" checked={checked} disabled={!hashOk} onChange={(e) => setChecked(e.target.checked)} />
              Я переглянув(-ла) документ, який підпишу</label>
            <div className="actions"><button type="button" className="btn" disabled={!checked || !hashOk || !canSubmit || Boolean(busy)}
              onClick={() => run("confirm", () => api(`${base}/submissions/${sub.id}/confirm`, { method: "POST", body: { sha256: sub.dpsSha256 } }))}>Далі: підпис ключем</button></div>
          </>
        ) : <SignWithKey sub={sub} bytes={bytes} parsed={parsed} base={base} busy={busy} run={run} company={company} />}
      </div>
    </>
  );
}

function SignWithKey({ sub, bytes, parsed, base, busy, run, company }) {
  const [keyFile, setKeyFile] = useState(null), [certFiles, setCertFiles] = useState([]), [password, setPassword] = useState("");
  const [status, setStatus] = useState(""), [err, setErr] = useState(null);
  const f = parsed.fields;
  const sign = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      setStatus("Відкриваємо ключ у браузері…");
      const { box, edrpou, drfo } = await loadKey({ keyFile, password, certFiles, log: setStatus });
      // idsFromBox: edrpou = drfo, якщо в сертифікаті немає ЄДРПОУ організації
      if (edrpou && edrpou !== drfo && edrpou !== company.edrpou) throw new Error(`Ключ належить компанії ${edrpou}, а накладна — ${company.edrpou}`);
      if (drfo && f.HKBOS && drfo !== f.HKBOS) throw new Error(`Підписати ПН має ${f.HBOS} (РНОКПП ${f.HKBOS}), а цей ключ — РНОКПП ${drfo}`);
      setStatus("Підписуємо документ…");
      const signature = await signBytes(box, bytes);
      setPassword(""); setStatus("Надсилаємо в ДПС…");
      await run("sign", () => api(`${base}/submissions/${sub.id}/sign`, { method: "POST", body: { signature } }));
    } catch (x) { setErr(x); } finally { setStatus(""); }
  };
  return (
    <form onSubmit={sign} className="pn-key">
      <p><b>Підпис:</b> {f.HBOS}, РНОКПП {f.HKBOS}. Ключ і пароль не залишають цю сторінку — на сервер іде лише підпис.</p>
      <label className="field">Файл ключа<input type="file" accept=".dat,.jks,.pfx,.zs2,.sk" onChange={(e) => setKeyFile(e.target.files[0] || null)} required /></label>
      <label className="field">Пароль ключа<input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" required /></label>
      <CertField onChange={setCertFiles} />
      {status && <p className="notice" role="status">{status}</p>}
      {err && <p className="error" role="alert">{err.message}</p>}
      <div className="actions"><button type="submit" className="btn" disabled={!keyFile || !password || Boolean(status) || Boolean(busy)}>Підписати й зареєструвати</button>
        <span className="muted small">Після цього накладна піде на реєстрацію в ЄРПН; змінити її вже не можна буде.</span></div>
    </form>
  );
}

function Result({ sub }) {
  const t = { sent: ["Надіслано. Чекаємо квитанцію ДПС", ""], registered: ["✓ Зареєстровано в ЄРПН", "pn-ok"],
    stopped: ["Реєстрацію зупинено", "error"], rejected: ["ДПС відмовила в реєстрації", "error"] }[sub.state];
  const receipts = Array.isArray(sub.dps?.receipts) ? sub.dps.receipts : Array.isArray(sub.dps?.receipts?.content) ? sub.dps.receipts.content : [];
  return (
    <section className="card" aria-labelledby="pn-res-h">
      <h3 id="pn-res-h" className={t[1]}>{t[0]}</h3>
      <p className="muted small">Документ у кабінеті № {sub.dpsDocId}{sub.sentAt ? ` · надіслано ${new Date(sub.sentAt).toLocaleString("uk-UA")}` : ""}{sub.signer?.subject?.commonName ? ` · підписав ${sub.signer.subject.commonName}` : ""}</p>
      {sub.state === "sent" && <p>ДПС обробляє ПН від кількох хвилин до доби (ваші ПН № 4 і № 2 чекали квитанцію 11 годин і до наступного дня). Сторінку можна закрити — стан збережено, перевірити можна будь-коли кнопкою нижче чи у вкладці «Реєстрація».</p>}
      {sub.state === "stopped" && <p>Подайте пояснення й копії документів у розділі СМКОР протягом 365 днів, або таблицю даних платника, щоб наступні ПН не зупинялись.</p>}
      {receipts.length > 0 && <ol className="pn-receipts">{receipts.map((k, i) => <li key={i}>{String(k.info ?? k.text ?? k.kvtText ?? k.message ?? JSON.stringify(k))}</li>)}</ol>}
    </section>
  );
}
